import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import VinculoDialog, { ItemVinculo } from '../componentes/VinculoDialog'
import DarAcessoDialog from '../componentes/DarAcessoDialog'
import { plural, podeVerEquipe, podeVerFicha, SEM_EQUIPE } from './comum'
import { useAuth } from '../../hooks/useAuth'
import { EquipeResumo } from './Pessoas'
import { AcessosNoAmbiente, DashboardComAcesso, TabelaAcessos } from './Pessoa'
import './gestao.scss'

interface Membro { id: number; nome: string; email: string; empresa?: string | null; ativa: boolean }
interface Detalhe { id: number; nome: string; membros: Membro[]; acessos: AcessosNoAmbiente[] }

/** Gestão › Equipes: quem está em cada equipe e o que ela vê. */
export default function Equipes() {
  const { id } = useParams()
  const atual = Number(id) || null
  const navigate = useNavigate()
  const { notify } = useNotification()
  const { getUserRoles } = useAuth()
  const roles = getUserRoles()
  const verEquipe = podeVerEquipe(roles), verFicha = podeVerFicha(roles)
  const [lista, setLista] = useState<EquipeResumo[] | null>(null)
  const [busca, setBusca] = useState('')
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [buscaMembro, setBuscaMembro] = useState('')
  const [janela, setJanela] = useState<'nova' | 'renomear' | 'excluir' | null>(null)
  const [nome, setNome] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erroJanela, setErroJanela] = useState<string | null>(null)
  const [addMembros, setAddMembros] = useState(false)
  const [todasPessoas, setTodasPessoas] = useState<ItemVinculo[] | null>(null)
  const [darAcesso, setDarAcesso] = useState(false)

  const carregarLista = useCallback(async () => {
    const r = await services.api.novoPortalEquipes()
    setLista(r.fail ? [] : r.data || [])
  }, [])
  const carregarDetalhe = useCallback(async () => {
    setErro(null)
    if (!atual) { setDetalhe(null); return }
    if (!verEquipe) { setDetalhe(null); setErro(SEM_EQUIPE); return }
    const r = await services.api.novoPortalEquipe(atual)
    if (r.fail) { setDetalhe(null); setErro(r.error || 'Equipe não encontrada.') } else setDetalhe(r.data)
  }, [atual, verEquipe])

  useEffect(() => { carregarLista() }, [carregarLista])
  useEffect(() => { setBuscaMembro(''); carregarDetalhe() }, [carregarDetalhe])
  useEffect(() => {
    if (!atual && lista?.length && window.innerWidth > 768) navigate(`/app/gestao/equipes/${lista[0].id}`, { replace: true })
  }, [atual, lista, navigate])

  const recarregar = () => { carregarDetalhe(); carregarLista() }
  const q = busca.trim().toLowerCase()
  const visiveis = (lista || []).filter(e => !q || e.nome.toLowerCase().includes(q))
  const qm = buscaMembro.trim().toLowerCase()
  const membros = (detalhe?.membros || []).filter(m => !qm || `${m.nome} ${m.email} ${m.empresa}`.toLowerCase().includes(qm))

  const abrirJanela = (j: 'nova' | 'renomear' | 'excluir') => { setNome(j === 'renomear' ? detalhe?.nome || '' : ''); setErroJanela(null); setJanela(j) }

  const confirmarJanela = async () => {
    setSalvando(true); setErroJanela(null)
    const r = janela === 'nova' ? await services.api.criarEquipe(nome.trim())
      : janela === 'renomear' ? await services.api.editarEquipe(nome.trim(), detalhe!.id)
        : await services.api.deleteEquipe(detalhe!.id)
    setSalvando(false)
    if (r.fail) { setErroJanela(r.error || 'Não foi possível salvar.'); return }
    const qual = janela
    setJanela(null)
    await carregarLista()
    if (qual === 'nova') { notify('Equipe criada. Agora adicione as pessoas.', { variant: 'success' }); if (r.data?.id) navigate(`/app/gestao/equipes/${r.data.id}`) }
    else if (qual === 'renomear') { notify('Nome alterado.', { variant: 'success' }); carregarDetalhe() }
    else { notify('Equipe excluída.', { variant: 'success' }); navigate('/app/gestao/equipes') }
  }

  const tirarMembro = async (m: Membro) => {
    const r = await services.api.removeUserFromTeam(detalhe!.id, m.id)
    if (r.fail) { notify(r.error || 'Não foi possível tirar da equipe.', { variant: 'error' }); return }
    notify(`${m.nome} saiu da equipe e perdeu os acessos que vinham dela.`, { variant: 'success' })
    recarregar()
  }
  const tirarAcesso = async (d: DashboardComAcesso) => {
    const r = await services.api.removeTeamFromDashboardGroup(d.acessoDiretoId)
    if (r.fail) { notify(r.error || 'Não foi possível tirar o acesso.', { variant: 'error' }); return }
    notify(`A equipe não vê mais ${d.dashboard}.`, { variant: 'success' })
    recarregar()
  }

  const abrirAddMembros = () => {
    setAddMembros(true)
    if (!todasPessoas) {
      services.api.getUsersAll({ orderByColumn: 'FullName' }, 1, 20000).then((r: any) => setTodasPessoas(r.fail ? [] : (r.data?.data || []).filter((u: any) => u.active).map((u: any) => ({
        id: String(u.id), nome: u.name, sub: [u.email, u.company].filter(Boolean).join(' · '), busca: `${u.email} ${u.company || ''} ${u.username || ''}`, tipo: u.internal ? 'interna' : 'cliente'
      }))))
    }
  }
  const jaMembros = new Set((detalhe?.membros || []).map(m => String(m.id)))
  const totalDash = (detalhe?.acessos || []).reduce((s, a) => s + a.dashboards.length, 0)

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Equipes</h1>
        <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => abrirJanela('nova')}><AddIcon />Nova equipe</button></div>
      </div>
      <p className="ng-explic">Dar acesso a uma equipe é melhor que a cada pessoa: quem entra depois já vê, quem sai deixa de ver.</p>

      <div className={`ng-ambientes${atual ? ' com-detalhe' : ''}`}>
        <aside className="ng-cartao ng-amb-lista" aria-label="Equipes">
          <label className="ng-busca"><SearchIcon fontSize="small" />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar equipe" aria-label="Buscar equipe" />
          </label>
          <div className="ng-amb-itens">
            {!lista && <p className="ng-explic">Carregando…</p>}
            {lista && visiveis.length === 0 && <p className="ng-explic">Nenhuma equipe.</p>}
            {visiveis.map(e => (
              <button key={e.id} type="button" className="ng-amb-item" aria-current={e.id === atual ? 'page' : undefined} onClick={() => navigate(`/app/gestao/equipes/${e.id}`)}>
                <span className="ng-amb-ini">{e.nome.charAt(0)}</span>
                <span className="ng-amb-txt"><b>{e.nome}</b><small>{plural(e.membros, 'pessoa', 'pessoas')} · {plural(e.dashboards, 'dashboard', 'dashboards')}</small></span>
              </button>
            ))}
          </div>
        </aside>

        <section className="ng-amb-detalhe">
          {erro && <div className="ng-cartao"><p className="ng-explic">{erro}</p></div>}
          {atual && !detalhe && !erro && <div className="ng-cartao"><p className="ng-explic">Carregando…</p></div>}
          {detalhe && (
            <>
              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <button type="button" className="ng-btn ng-voltar" onClick={() => navigate('/app/gestao/equipes')}>‹ Equipes</button>
                  <h2>{detalhe.nome}</h2>
                  <div className="ng-acoes">
                    <button type="button" className="ng-btn" onClick={() => abrirJanela('renomear')}>Renomear</button>
                    <button type="button" className="ng-btn perigo" onClick={() => abrirJanela('excluir')}>Excluir</button>
                  </div>
                </div>
                <p className="ng-explic">{plural(detalhe.membros.length, 'pessoa', 'pessoas')} · vê {plural(totalDash, 'dashboard', 'dashboards')}</p>
              </div>

              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <h2>Pessoas</h2>
                  <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={abrirAddMembros}><AddIcon />Adicionar pessoas</button></div>
                </div>
                {detalhe.membros.length > 8 && (
                  <div className="ng-filtros" style={{ marginBottom: 8 }}>
                    <label className="ng-busca"><SearchIcon fontSize="small" />
                      <input value={buscaMembro} onChange={e => setBuscaMembro(e.target.value)} placeholder="Buscar pessoa" aria-label="Buscar pessoa na equipe" />
                    </label>
                  </div>
                )}
                <div className="ng-lista" style={{ maxHeight: 460 }}>
                  {detalhe.membros.length === 0 && <p className="ng-explic">Ninguém na equipe ainda.</p>}
                  {membros.map(m => (
                    <div key={m.id} className="ng-linha">
                      <span className="ng-amb-ini">{m.nome.charAt(0)}</span>
                      <span className="ng-amb-txt">{verFicha ? <Link to={`/app/gestao/pessoas/${m.id}`}><b>{m.nome}</b></Link> : <b>{m.nome}</b>}<small>{[m.email, m.empresa].filter(Boolean).join(' · ')}{m.ativa ? '' : ' · inativa'}</small></span>
                      <button type="button" className="ng-ico perigo" onClick={() => tirarMembro(m)} aria-label={`Tirar ${m.nome} da equipe`} title="Tirar da equipe"><CloseIcon fontSize="small" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <h2>O que a equipe vê</h2>
                  <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => setDarAcesso(true)}><AddIcon />Dar acesso</button></div>
                </div>
                <p className="ng-explic">Todos os membros veem estes dashboards, com o recorte da regra de cada um.</p>
                <TabelaAcessos acessos={detalhe.acessos} aoTirar={tirarAcesso} rotuloDireto="da equipe" />
              </div>
            </>
          )}
        </section>
      </div>

      <Janela aberto={janela === 'nova' || janela === 'renomear'} titulo={janela === 'nova' ? 'Nova equipe' : 'Renomear equipe'} botao={janela === 'nova' ? 'Criar equipe' : 'Salvar'}
              desabilitado={nome.trim().length < 2 || (janela === 'renomear' && nome.trim() === detalhe?.nome)} salvando={salvando} erro={erroJanela}
              aoConfirmar={confirmarJanela} aoFechar={() => setJanela(null)}>
        <label className="nv-campo"><span>Nome</span><input value={nome} maxLength={100} onChange={e => setNome(e.target.value)} placeholder="Ex.: Supervisores Sul" /></label>
      </Janela>

      <Janela aberto={janela === 'excluir'} titulo="Excluir equipe" botao="Excluir de vez" perigo salvando={salvando} erro={erroJanela} aoConfirmar={confirmarJanela} aoFechar={() => setJanela(null)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Excluir <b>{detalhe?.nome}</b>? As {plural(detalhe?.membros.length || 0, 'pessoa', 'pessoas')} perdem os {plural(totalDash, 'acesso', 'acessos')} que vinham da equipe. Os acessos diretos de cada pessoa continuam.</p>
      </Janela>

      {detalhe && (
        <VinculoDialog
          aberto={addMembros}
          titulo="Adicionar pessoas"
          sub={`Equipe ${detalhe.nome}`}
          carregando={!todasPessoas}
          abas={[{
            id: 'p', rotulo: 'Pessoas', dica: 'Buscar por nome, e-mail ou empresa', unidade: ['pessoa', 'pessoas'],
            itens: (todasPessoas || []).map(i => ({ ...i, ja: jaMembros.has(i.id) ? 'Já está' : undefined })),
            filtros: [
              { id: 'interna', rotulo: 'Internas', teste: (i: ItemVinculo) => i.tipo === 'interna' },
              { id: 'cliente', rotulo: 'Clientes', teste: (i: ItemVinculo) => i.tipo === 'cliente' }
            ]
          }]}
          resumo={(s, total) => total ? <><b>{plural(total, 'pessoa', 'pessoas')}</b> {total === 1 ? 'entra' : 'entram'} em <b>{detalhe.nome}</b> e {total === 1 ? 'passa' : 'passam'} a ver {plural(totalDash, 'dashboard', 'dashboards')}.</> : <>Escolha quem entra na equipe.</>}
          botao={total => total ? `Adicionar ${plural(total, 'pessoa', 'pessoas')}` : 'Adicionar'}
          aoFechar={() => setAddMembros(false)}
          aoConfirmar={async s => {
            const r = await services.api.vicularUsuarioComEquipeMult((s.p || []).map(i => ({ teamId: detalhe.id, userId: Number(i.id) })))
            if (r.fail) return r.error || 'Não foi possível adicionar.'
            setAddMembros(false)
            notify('Pessoas adicionadas à equipe.', { variant: 'success' })
            recarregar()
            return null
          }}
        />
      )}

      {detalhe && (
        <DarAcessoDialog aberto={darAcesso} equipes={[detalhe.id]} quem={`a equipe ${detalhe.nome}`} ja={detalhe.acessos.flatMap(a => a.dashboards.map(d => d.vinculoId))}
                         aoFechar={() => setDarAcesso(false)} aoDar={m => { setDarAcesso(false); notify(m, { variant: 'success' }); recarregar() }} />
      )}
    </div>
  )
}
